import { imgixHost } from 'src/config'
import Generic from 'src/models/generic/Generic'
import InventoryRecipe from 'src/models/inventory-recipe/InventoryRecipe'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, Integer, IsoDateTime, JsonObject, MODEL_LEVEL } from 'src/models/Model'
import ModifierSet from 'src/models/modifier-set/ModifierSet'
export default class Modifier extends Model {
  static name = 'Modifier'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static duplicatable = true

  static paginate = true

  static hasAuditSummary = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      action: {
        type: String,
        input: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      image: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      },
      description: {
        type: String,
        default: null,
        translatable: true
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      cost: {
        type: Number,
        encode: encoders.cents,
        default: null
      },
      availableOnline: {
        type: Boolean,
        default: true
      },
      disableEditCostCents: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      salesDisabled: {
        type: Boolean,
        default: false
      },
      modifierSets: {
        type: [ModifierSet],
        reference: true,
        default: () => ([])
      },
      toBeDeleted: {
        type: Boolean,
        output: true,
        default: false
      },
      archivedAt: {
        type: IsoDateTime,
        output: true
      },
      hasRecipe: {
        type: Boolean,
        output: true,
        default: false
      },
      inventoryRecipes: {
        type: [InventoryRecipe],
        output: true,
        excludeFromDefault: true
      },
      inventoryAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      locations: {
        type: [Location],
        output: true,
        excludeFromDefault: true
      },
      libraryParentId: {
        type: ID,
        default: null,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      active () {
        return _.isNil(this.archivedAt) && !this.toBeDeleted
      },
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=100&h100&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}`
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      },
      nameWithPrice () {
        if (this.price === 0) {
          return this.name
        } else {
          return `${this.name} (${this.i18n.n(this.price, 'currency')})`
        }
      },
      trackStock () {
        return this.hasRecipe || this.action === 'manage_inventory'
      }
    }

    this.queries = {
      modifierAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        results: {
          value: Generic
        }
      }
    }
  }

  invalidInventoryRecipeIds (selectedItemVariationId = null) {
    if (!selectedItemVariationId) return []

    /**
     * Recipe triggers that specify an unselected item variation are invalid.
     */
    return _(this.inventoryRecipes.data)
      .map(inventoryRecipe => {
        return _.filter(inventoryRecipe.inventoryRecipeTriggers.data, inventoryRecipeTrigger => {
          return inventoryRecipeTrigger.itemVariationId && inventoryRecipeTrigger.itemVariationId !== selectedItemVariationId
            ? inventoryRecipe.id
            : null
        })
      })
      .flatten()
      .uniq()
      .value()
  }

  availableStock ({
    availabilitiesAndConstraints,
    deliveryType,
    item,
    screen,
    selectedItemVariationId = null
  }) {
    if (!this.inventoryStrategy || !this.hasRecipe || !availabilitiesAndConstraints) return null

    const sourceLocationKey = this.inventoryStrategy.sourceLocationKey({ deliveryType, item, screen })
    const sourceLocationId = this.inventoryStrategy.sourceLocationId({ deliveryType, item, screen })

    return _(availabilitiesAndConstraints[this.id][sourceLocationKey][sourceLocationId])
      .entries()
      .map(([inventoryRecipeId, availabilityAndConstraintsObject]) => {
        return _.includes(this.invalidInventoryRecipeIds(selectedItemVariationId), +inventoryRecipeId)
          ? null
          : `${availabilityAndConstraintsObject.available}`
      })
      .compact()
      .max()
  }

  isOutOfStock ({
    availabilitiesAndConstraints,
    deliveryType,
    item,
    screen,
    selectedItemVariationId = null
  }) {
    if (!this.inventoryStrategy || !this.hasRecipe || !availabilitiesAndConstraints) return false

    return this.availableStock({
      availabilitiesAndConstraints,
      deliveryType,
      item,
      screen,
      selectedItemVariationId
    }) <= 0
  }
}
